import { FormattedMessage, useIntl } from "react-intl";
import { Loader2, Sparkles } from "lucide-react";

import { Button, Card, CardContent } from "@runcheetah/ui";

export type RunQuote = {
  runsRequired: number;
  runsRemaining: number | null;
  runsRemainingAfter: number | null;
};

const formatNumber = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale).format(value);

export function RunConfirmationDialog({
  open,
  title,
  description,
  confirmLabel,
  upgradeLabel,
  quote,
  quotePending,
  confirmPending,
  error,
  onUpgrade,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description?: string;
  confirmLabel: string;
  upgradeLabel?: string;
  quote: RunQuote | null;
  quotePending: boolean;
  confirmPending: boolean;
  error?: string | null;
  onUpgrade?: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const intl = useIntl();

  if (!open) {
    return null;
  }

  const hasEnoughRuns =
    quote?.runsRemaining === null ||
    quote?.runsRequired === undefined ||
    quote.runsRemaining >= quote.runsRequired;
  const confirmDisabled = quotePending || confirmPending || !quote || !hasEnoughRuns;
  const showUpgradeAction = Boolean(
    onUpgrade && !quotePending && (!hasEnoughRuns || (error && !quote)),
  );

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm">
      <Card className="w-full max-w-md rounded-3xl border-border/70 shadow-xl">
        <CardContent className="space-y-5 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary">
              <Sparkles className="h-5 w-5" aria-hidden="true" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold tracking-tight text-foreground">{title}</h2>
              {description ? (
                <p className="mt-1 text-sm leading-6 text-muted-foreground">{description}</p>
              ) : null}
            </div>
          </div>

          <div className="rounded-2xl border border-border/70 bg-muted/35 p-4">
            {quotePending ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <FormattedMessage
                  id="runConfirmation.loading"
                  defaultMessage="Checking available runs..."
                />
              </div>
            ) : quote ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">
                    <FormattedMessage id="runConfirmation.cost" defaultMessage="Run cost" />
                  </span>
                  <span className="font-semibold text-foreground">
                    <FormattedMessage
                      id="runConfirmation.runsRequired"
                      defaultMessage="{count, plural, one {# run} other {# runs}}"
                      values={{ count: quote.runsRequired }}
                    />
                  </span>
                </div>
                {quote.runsRemaining !== null ? (
                  <div className="flex items-center justify-between gap-3 text-sm">
                    <span className="text-muted-foreground">
                      <FormattedMessage
                        id="runConfirmation.remainingAfter"
                        defaultMessage="Remaining after"
                      />
                    </span>
                    <span className="font-semibold text-foreground">
                      {formatNumber(quote.runsRemainingAfter ?? 0, intl.locale)}
                    </span>
                  </div>
                ) : null}
                {!hasEnoughRuns ? (
                  <p className="text-sm leading-5 text-destructive">
                    <FormattedMessage
                      id="runConfirmation.insufficient"
                      defaultMessage="You need {required} runs, but only {remaining} remain."
                      values={{
                        required: quote.runsRequired,
                        remaining: quote.runsRemaining ?? 0,
                      }}
                    />
                  </p>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                <FormattedMessage
                  id="runConfirmation.unavailable"
                  defaultMessage="Run estimate is unavailable."
                />
              </p>
            )}
          </div>

          {error ? (
            <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" onClick={onCancel} disabled={confirmPending}>
              <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
            </Button>
            {showUpgradeAction ? (
              <Button type="button" onClick={onUpgrade} disabled={confirmPending}>
                {upgradeLabel ?? (
                  <FormattedMessage id="runConfirmation.upgrade" defaultMessage="Upgrade" />
                )}
              </Button>
            ) : (
              <Button type="button" onClick={onConfirm} disabled={confirmDisabled}>
                {confirmPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                {confirmLabel}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
